'use client';

import { Check, Sparkles } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useCandidateAnalytics } from '@/features/candidate-interview';
import type { ReactNode } from 'react';
import { cn } from '@/shared/utils';
export function CandidateMessage({
	interviewId,
	title,
	text,
	illustration,
	compact = false,
	children,
}: {
	interviewId?: string;
	title: string;
	text?: ReactNode;
	illustration?: 'start' | 'complete';
	compact?: boolean;
	children?: ReactNode;
}) {
	const track = useCandidateAnalytics(interviewId ?? '', 'complete');
	const seen = useRef(false);
	useEffect(() => {
		if (interviewId && !seen.current) {
			seen.current = true;
			track('page_view');
		}
	}, [interviewId, track]);
	return (
		<main
			className={cn(
				'flex h-full min-h-0 flex-1 flex-col overflow-auto bg-white px-4 sm:px-6 lg:px-8',
				compact && 'h-auto flex-none',
			)}
		>
			<div
				className={cn(
					'm-auto flex min-h-[560px] w-full max-w-xl shrink-0 flex-col items-center justify-center py-16 text-center',
					compact && 'min-h-0 py-12',
				)}
			>
				{illustration === 'start' && (
					<div className="flex size-20 shrink-0 items-center justify-center rounded-3xl bg-gray-1 text-white shadow-lg shadow-gray-1/15">
						<Sparkles
							size={40}
							className="size-10 shrink-0"
							aria-hidden="true"
						/>
					</div>
				)}
				{illustration === 'complete' && (
					<div className="flex size-20 shrink-0 items-center justify-center rounded-full bg-green/10 text-green-600">
						<Check size={44} className="size-11 shrink-0" aria-hidden="true" />
					</div>
				)}
				<h1 className="mt-8 text-4xl font-bold tracking-tight text-gray-1 sm:text-[42px]">
					{title}
				</h1>
				{text && (
					<div className="mt-6 space-y-3 text-pretty text-lg font-medium leading-8 text-gray-500">
						{typeof text === 'string' ? (
							<p className="whitespace-pre-wrap">{text}</p>
						) : (
							text
						)}
					</div>
				)}
				{children && <div className="mt-9">{children}</div>}
			</div>
		</main>
	);
}
